import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTurnkeyWallet } from '@/providers/TurnkeyProvider';
import { useWalletBalance } from '@/hooks/useWalletBalance';
import { useTokenBalances } from '@/hooks/useTokenBalances';
import { TokenSelector } from '@/components/wallet/TokenSelector';
import { SwapTab } from '@/components/wallet/SwapTab';
import { DepositTab } from '@/components/wallet/DepositTab';
import { WithdrawTab } from '@/components/wallet/WithdrawTab';
import { ImportTab } from '@/components/wallet/ImportTab';
import { ExportTab } from '@/components/wallet/ExportTab';
import { useSolPrice } from '@/hooks/useSOLPrice';
import { useIsMobile } from '@/hooks/use-mobile';
import { Loader2, ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, RefreshCw, Key, Download, QrCode, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import type { WalletToken } from '@/hooks/useTokenBalances';

interface WalletModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const WalletModal = ({ open, onOpenChange }: WalletModalProps) => {
  const {
    publicKey,
    connected,
    signTransaction,
    sendTransaction,
    exportWallet,
    importWallet,
    allSolanaWallets,
    setActiveWallet,
  } = useTurnkeyWallet();
  const { balance, formattedBalance, isLoading: balanceLoading, isFetching, refetch } = useWalletBalance();
  const { tokens: splTokens, isLoading: tokensLoading, refetch: refetchTokens } = useTokenBalances();
  const { data: solPriceData } = useSolPrice();
  const isMobile = useIsMobile();

  // Calculate USD balance
  const solPrice = solPriceData?.price;
  const usdBalance = solPrice && balance !== null
    ? (balance * solPrice).toFixed(2)
    : null;
  const [selectedToken, setSelectedToken] = useState<WalletToken | null>(null);
  const [showQROverlay, setShowQROverlay] = useState(false);
  const [activeTab, setActiveTab] = useState('deposit');
  const scrollableRef = useRef<HTMLDivElement>(null);

  const walletAddress = publicKey?.toBase58() || '';
  const truncatedAddress = walletAddress
    ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`
    : '';

  // Refetch balance and tokens when modal opens or wallet changes
  useEffect(() => {
    if (open && walletAddress) {
      refetch();
      refetchTokens();
    }
  }, [open, walletAddress, refetch, refetchTokens]);

  // Reset QR overlay and token selection when modal closes
  useEffect(() => {
    if (!open) {
      setShowQROverlay(false);
      setSelectedToken(null);
    }
  }, [open]);

  // iOS keyboard handling for vaul Drawer.
  // iOS Safari scrolls the *page* when focusing inputs inside position:fixed + transform
  // elements (like vaul's Drawer), pushing the entire drawer off-screen. We disable vaul's
  // built-in handling (repositionInputs={false}) and instead:
  // 1. Intercept and undo any page scroll iOS applies
  // 2. Add bottom padding so the container has enough scroll room above the keyboard
  // 3. Manually scroll the input into view within the drawer's scroll container
  //    using scrollTop math (NOT scrollIntoView, which scrolls the page)
  useEffect(() => {
    if (!isMobile || !open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const container = scrollableRef.current;
    if (!container) return;

    const initialHeight = vv.height;
    let rafId: number;

    const isDrawerInput = () => {
      const el = document.activeElement;
      return el instanceof HTMLElement &&
        (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') &&
        container.contains(el);
    };

    const resetPageScroll = () => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };

    // Use scrollTop math to position the input in the visible area above the keyboard.
    // scrollIntoView is intentionally avoided — it scrolls the page, not the container,
    // when the container is inside a position:fixed + transform element.
    const scrollInputIntoContainer = () => {
      if (!isDrawerInput()) return;
      const active = document.activeElement as HTMLElement;
      const activeRect = active.getBoundingClientRect();

      // Position the input in the upper third of the visible viewport
      const targetY = vv.height * 0.3;
      const diff = activeRect.top - targetY;
      if (Math.abs(diff) > 10) {
        container.scrollTop = Math.max(0, container.scrollTop + diff);
      }
    };

    const handleViewportChange = () => {
      resetPageScroll();

      // Add bottom padding so the container can scroll inputs above the keyboard
      const keyboardHeight = initialHeight - vv.height;
      if (keyboardHeight > 100) {
        container.style.paddingBottom = `${keyboardHeight}px`;
      } else {
        container.style.paddingBottom = '';
      }

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        resetPageScroll();
        scrollInputIntoContainer();
      });
    };

    // Intercept page scrolls iOS applies while a drawer input is focused
    const handleWindowScroll = () => {
      if (isDrawerInput()) {
        resetPageScroll();
      }
    };

    // After keyboard animation completes (~300ms), ensure input is positioned
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (!container.contains(target)) return;
      if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') return;

      setTimeout(() => {
        resetPageScroll();
        scrollInputIntoContainer();
      }, 350);
    };

    vv.addEventListener('resize', handleViewportChange);
    vv.addEventListener('scroll', handleViewportChange);
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      vv.removeEventListener('resize', handleViewportChange);
      vv.removeEventListener('scroll', handleViewportChange);
      window.removeEventListener('scroll', handleWindowScroll);
      document.removeEventListener('focusin', handleFocusIn);
      cancelAnimationFrame(rafId);
      container.style.paddingBottom = '';
    };
  }, [isMobile, open]);

  // Pre-sign callback for modal: close modal + brief delay before Turnkey signing overlay appears
  const modalPreSign = async () => {
    onOpenChange(false);
    await new Promise(resolve => setTimeout(resolve, 100));
  };

  // QR Overlay view
  const qrOverlayContent = (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setShowQROverlay(false)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">Receive Address</span>
      </div>

      <div className="flex flex-col items-center gap-4 flex-1">
        <div className="p-4 bg-white rounded-xl shadow-sm">
          <QRCode
            value={walletAddress}
            size={isMobile ? 160 : 200}
            level="M"
            bgColor="#FFFFFF"
            fgColor="#000000"
          />
        </div>

        <p className="text-xs font-mono text-muted-foreground text-center break-all px-4">
          {walletAddress}
        </p>
      </div>

      <CopyButton
        value={walletAddress}
        className="w-full mt-4"
        variant="outline"
        label="Copy Address"
        copiedLabel="Address Copied!"
        onCopy={() => toast.success('Address copied to clipboard')}
      />
    </div>
  );

  // Tab content area
  const tabsContent = (
    <Tabs
      value={activeTab}
      onValueChange={(value) => {
        setActiveTab(value);
        setShowQROverlay(false);
      }}
      className="w-full"
    >
      <TabsList className={isMobile ? "flex w-full h-auto overflow-x-auto flex-nowrap" : "grid w-full grid-cols-5 h-auto"}>
        <TabsTrigger value="deposit" className={isMobile ? "min-h-[44px] min-w-[44px] flex-1" : "gap-1.5 text-sm py-2.5 px-4"}>
          <ArrowDownToLine className="h-4 w-4 flex-shrink-0" />
          {!isMobile && "Deposit"}
        </TabsTrigger>
        <TabsTrigger value="withdraw" className={isMobile ? "min-h-[44px] min-w-[44px] flex-1" : "gap-1.5 text-sm py-2.5 px-4"}>
          <ArrowUpFromLine className="h-4 w-4 flex-shrink-0" />
          {!isMobile && "Withdraw"}
        </TabsTrigger>
        <TabsTrigger value="swap" className={isMobile ? "min-h-[44px] min-w-[44px] flex-1" : "gap-1.5 text-sm py-2.5 px-4"}>
          <ArrowLeftRight className="h-4 w-4 flex-shrink-0" />
          {!isMobile && "Swap"}
        </TabsTrigger>
        <TabsTrigger value="import" className={isMobile ? "min-h-[44px] min-w-[44px] flex-1" : "gap-1.5 text-sm py-2.5 px-4"}>
          <Download className="h-4 w-4 flex-shrink-0" />
          {!isMobile && "Import"}
        </TabsTrigger>
        <TabsTrigger value="export" className={isMobile ? "min-h-[44px] min-w-[44px] flex-1" : "gap-1.5 text-sm py-2.5 px-4"}>
          <Key className="h-4 w-4 flex-shrink-0" />
          {!isMobile && "Export"}
        </TabsTrigger>
      </TabsList>

      <div className={isMobile ? "mt-4" : "mt-5"}>
        <TabsContent value="deposit" className="flex flex-col mt-0">
          <DepositTab walletAddress={walletAddress} selectedToken={selectedToken} />
        </TabsContent>

        <TabsContent value="withdraw" className="flex flex-col mt-0">
          <WithdrawTab
            publicKey={publicKey}
            sendTransaction={sendTransaction}
            balance={balance}
            selectedToken={selectedToken}
            refetchBalance={refetch}
            refetchTokens={refetchTokens}
            onPreSign={modalPreSign}
          />
        </TabsContent>

        <TabsContent value="swap" className="flex flex-col mt-0">
          {signTransaction && (
            <SwapTab
              walletAddress={walletAddress}
              signTransaction={signTransaction}
              connected={connected}
              balance={balance}
              splTokens={splTokens}
              formattedBalance={formattedBalance}
              tokensLoading={tokensLoading}
              isMobile={isMobile}
              refetchBalance={refetch}
              refetchTokens={refetchTokens}
            />
          )}
        </TabsContent>

        <TabsContent value="import" className="flex flex-col mt-0">
          <ImportTab
            importWallet={importWallet}
            allSolanaWallets={allSolanaWallets}
            setActiveWallet={setActiveWallet}
            onPreAction={modalPreSign}
          />
        </TabsContent>

        <TabsContent value="export" className="flex flex-col mt-0">
          <ExportTab
            exportWallet={exportWallet}
            onPreAction={modalPreSign}
          />
        </TabsContent>
      </div>
    </Tabs>
  );

  // Shared modal inner content
  const modalContent = (
    <div className={isMobile ? "space-y-3" : "space-y-5"}>
      {/* Balance */}
      <div className={isMobile ? "rounded-lg border border-border bg-secondary/50 p-3 text-center" : "rounded-xl border border-border bg-secondary/50 p-5 text-center"}>
        <div className={isMobile ? "flex items-center justify-center mb-0.5" : "flex items-center justify-center mb-1.5"}>
          <TokenSelector
            tokens={splTokens}
            solBalance={formattedBalance}
            selectedToken={selectedToken}
            onSelect={setSelectedToken}
            isLoading={tokensLoading}
          />
        </div>
        <div className={isMobile ? "text-xl font-bold" : "text-3xl font-bold"}>
          {selectedToken === null ? (
            // SOL selected
            balanceLoading ? (
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            ) : (
              <>
                {formattedBalance} SOL
                {usdBalance && (
                  <span className={isMobile ? "text-lg text-muted-foreground ml-2 font-normal" : "text-xl text-muted-foreground ml-2 font-normal"}>
                    (~${usdBalance})
                  </span>
                )}
              </>
            )
          ) : (
            // SPL token selected
            <>{selectedToken.uiAmountString} {selectedToken.symbol}</>
          )}
        </div>
      </div>

      {/* Compact address row */}
      <div className="flex items-center gap-3">
        <span className={isMobile ? "text-sm font-mono text-muted-foreground" : "text-base font-mono text-muted-foreground"}>{truncatedAddress}</span>
        <div className="flex items-center gap-1 ml-auto">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setShowQROverlay(true)}
          >
            <QrCode className="h-4 w-4" />
          </Button>
          <CopyButton
            value={walletAddress}
            iconOnly
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onCopy={() => toast.success('Address copied to clipboard')}
          />
        </div>
      </div>

      {/* QR overlay or tabs */}
      {showQROverlay ? qrOverlayContent : tabsContent}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
        <DrawerContent className="px-4 pb-6">
          <DrawerHeader className="text-left">
            <DrawerTitle className="flex items-center gap-2">
              Wallet
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 min-w-[44px] min-h-[44px]"
                onClick={() => { refetch(); refetchTokens(); }}
                disabled={isFetching}
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              Manage your wallet: view balances, deposit tokens, withdraw funds, or export your private key
            </DrawerDescription>
          </DrawerHeader>
          <div ref={scrollableRef} style={{ overflowY: 'auto', maxHeight: 'calc(85dvh - 80px)' }}>
            {modalContent}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" aria-describedby="wallet-dialog-description">
        <DialogDescription id="wallet-dialog-description" className="sr-only">
          Manage your wallet: view balances, deposit tokens, withdraw funds, or export your private key
        </DialogDescription>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Wallet
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => { refetch(); refetchTokens(); }}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </DialogTitle>
        </DialogHeader>
        {modalContent}
      </DialogContent>
    </Dialog>
  );
};
